// src/pages/Documentos.jsx - V7 ENTERPRISE MODULAR
// ═══════════════════════════════════════════════════════════════════════════
// 🎯 ORQUESTADOR — Airbnb/Zillow/QuintoAndar Grade
// ═══════════════════════════════════════════════════════════════════════════
// 🔥 V8: FIX — Modales siempre renderizados (fuera de condicionales)
// 🔥 V7: Ajustado — condición de error usa solo 'error' (ctxReady ahora derivado)
// 🔥 V7: Añadido — botón de recarga sin refrescar la página completa
// 🔥 V6: Arquitectura modular — 1 hook + 8 componentes puros
// 🔥 V6: Zero lógica de negocio en el render (todo en useDocumentos)
// 🔥 V6: Tiempo real vía Observer único de DataContext
// 🔥 V6: Cero race conditions — anti-duplicados, anti-concurrentes
// ═══════════════════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Plus, Search } from 'lucide-react';
import { useIsMobile } from '../hooks/useMediaQuery';
import { useDocumentos } from '../hooks/useDocumentos';
import DocumentStats from '../components/documents/DocumentStats';
import DocumentFilters from '../components/documents/DocumentFilters';
import DocumentGrid from '../components/documents/DocumentGrid';
import DocumentSkeleton from '../components/documents/DocumentSkeleton';
import DocumentModal from '../components/documents/DocumentModal';
import DocumentPreview from '../components/documents/DocumentPreview';
import DocumentConfirmModal from '../components/documents/DocumentConfirmModal';

// ═══════════════════════════════════════════════════════════════════════════
// 🎯 COMPONENTE PRINCIPAL (SOLO RENDERIZA)
// ═══════════════════════════════════════════════════════════════════════════

export default function Documentos() {
  const isMobile = useIsMobile();
  // 🔥 V7: Key forzada para reiniciar el hook en recarga sin F5
  const [hookKey, setHookKey] = useState(0);

  // ─── Hook central (toda la lógica) ──────────────────────────────────
  const {
    documents,
    clients,
    properties,
    stats,
    chartData,
    chartGrowth,
    filteredAndSortedDocs,
    loading,
    error,
    showModal,
    setShowModal,
    previewDoc,
    setPreviewDoc,
    previewLoading,
    deleteModal,
    setDeleteModal,
    showChart,
    setShowChart,
    showFilters,
    setShowFilters,
    selectedDocs,
    search,
    setSearch,
    filterType,
    setFilterType,
    filterClient,
    setFilterClient,
    filterProperty,
    setFilterProperty,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    resetFilters,
    form,
    setForm,
    uploading,
    fileInputRef,
    searchInputRef,
    openNewModal,
    handleSave,
    handleFileChange,
    handlePreview,
    handleDownload,
    handleDelete,
    confirmDelete,
    toggleFavorite,
    toggleSelect,
    selectAll,
    bulkDelete,
    getFileTypeInfo,
    formatFileSize,
  } = useDocumentos();

  // ─── Primary color ──────────────────────────────────────────────────
  const primaryColor = useMemo(() => {
    if (typeof window === 'undefined') return '#2563eb';
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--primary-color').trim() || '#2563eb';
  }, []);

  // ─── Atajos de teclado ──────────────────────────────────────────────
  useEffect(() => {
    if (isMobile) return;
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'n') { e.preventDefault(); openNewModal(); }
      if (e.ctrlKey && e.key === 'k') { e.preventDefault(); searchInputRef.current?.focus(); }
      if (e.key === 'Escape' && previewDoc) setPreviewDoc(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewDoc, isMobile, openNewModal, setPreviewDoc, searchInputRef]);

  // ─── Recarga suave (sin F5) ────────────────────────────────────────
  const handleRetry = () => setHookKey(k => k + 1);

  // ═══════════════════════════════════════════════════════════════════════
  // 🎨 RENDER
  // ═══════════════════════════════════════════════════════════════════════

  return (
    <>
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* MODALES — SIEMPRE RENDERIZADOS (V8 FIX)                         */}
      {/* ═══════════════════════════════════════════════════════════════ */}

      {/* CONFIRM DELETE MODAL */}
      <AnimatePresence>
        {deleteModal.isOpen && (
          <DocumentConfirmModal
            isOpen={deleteModal.isOpen}
            title="Excluir documento"
            message={`Tem certeza que deseja excluir "${deleteModal.doc?.title}"?`}
            confirmLabel="Confirmar exclusão"
            cancelLabel="Cancelar"
            onConfirm={confirmDelete}
            onCancel={() => setDeleteModal({ isOpen: false, doc: null })}
            isMobile={isMobile}
          />
        )}
      </AnimatePresence>

      {/* MODAL CREAR/EDITAR */}
      <DocumentModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        form={form}
        onFormChange={setForm}
        clients={clients}
        properties={properties}
        isMobile={isMobile}
        uploading={uploading}
        onFileChange={handleFileChange}
        onSave={handleSave}
        fileInputRef={fileInputRef}
        primaryColor={primaryColor}
        isEditing={!!form.id}
      />

      {/* MODAL PREVIEW */}
      <DocumentPreview
        doc={previewDoc}
        isOpen={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        onDownload={handleDownload}
        isMobile={isMobile}
        primaryColor={primaryColor}
        formatFileSize={formatFileSize}
        getFileTypeInfo={getFileTypeInfo}
      />

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* RUTAS DE CONTENIDO                                               */}
      {/* ═══════════════════════════════════════════════════════════════ */}

      {/* 🔥 V7: Skeleton mientras carga */}
      {loading && <DocumentSkeleton key={hookKey} isMobile={isMobile} />}

      {/* 🔥 V7: Error */}
      {!loading && error && (
        <div className={isMobile ? 'p-3' : 'p-6 max-w-7xl mx-auto'}>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-rose-200 rounded-2xl shadow-lg p-8 flex flex-col items-center gap-4 text-center"
          >
            <div className="p-4 bg-rose-100 rounded-full">
              <FileText className="w-8 h-8 text-rose-500" />
            </div>
            <div>
              <p className="font-semibold text-slate-800 text-lg">Erro ao carregar documentos</p>
              <p className="text-slate-500 text-sm mt-1">{error}</p>
            </div>
            <button
              onClick={handleRetry}
              className="px-5 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 active:scale-[0.98] transition-all shadow-md"
            >
              Tentar novamente
            </button>
          </motion.div>
        </div>
      )}

      {/* 🔥 V7: Estado vacío */}
      {!loading && !error && documents.length === 0 && (
        <div className={isMobile ? 'p-3 space-y-4 max-w-7xl mx-auto' : 'p-4 md:p-6 space-y-6 max-w-7xl mx-auto'}>
          {/* HEADER */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div>
              <h1 className={`font-bold text-slate-900 flex items-center gap-2 ${isMobile ? 'text-xl' : 'text-2xl md:text-3xl'}`}>
                <FileText className={isMobile ? 'w-5 h-5' : 'w-6 h-6 md:w-7 md:h-7'} style={{ color: primaryColor }} /> Documentos
              </h1>
            </div>
            <button
              onClick={openNewModal}
              className={`inline-flex items-center gap-2 text-white rounded-xl font-medium shadow-md transition-all active:scale-[0.98] ${isMobile ? 'px-4 py-3' : 'px-4 py-3 md:py-2'}`}
              style={{ backgroundColor: primaryColor }}
              aria-label="Novo documento"
            >
              <Plus size={isMobile ? 18 : 20} />
              <span className={isMobile ? 'sr-only' : 'hidden sm:inline'}>Novo</span>
            </button>
          </div>

          {/* VACÍO */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-slate-200 rounded-2xl shadow-sm p-10 flex flex-col items-center gap-4 text-center"
          >
            <div className="p-5 bg-slate-100 rounded-full">
              <FileText className="w-10 h-10 text-slate-400" />
            </div>
            <div>
              <p className="font-semibold text-slate-700 text-lg">Nenhum documento</p>
              <p className="text-slate-500 text-sm mt-1 max-w-xs">
                Clique em <strong>Novo</strong> para adicionar contratos, fotos ou documentos.
              </p>
            </div>
            <button
              onClick={openNewModal}
              className="px-5 py-2.5 rounded-xl text-white text-sm font-medium shadow-md active:scale-[0.98] transition-all"
              style={{ backgroundColor: primaryColor }}
            >
              <Plus size={16} className="inline mr-1.5" />
              Novo documento
            </button>
          </motion.div>
        </div>
      )}

      {/* 🔥 V7: Render normal — datos disponibles */}
      {!loading && !error && documents.length > 0 && (
        <div className={isMobile ? 'p-3 space-y-4 max-w-7xl mx-auto' : 'p-4 md:p-6 space-y-6 max-w-7xl mx-auto'}>
          {/* HEADER */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div>
              <h1 className={`font-bold text-slate-900 flex items-center gap-2 ${isMobile ? 'text-xl' : 'text-2xl md:text-3xl'}`}>
                <FileText className={isMobile ? 'w-5 h-5' : 'w-6 h-6 md:w-7 md:h-7'} style={{ color: primaryColor }} /> Documentos
              </h1>
              {!isMobile && (
                <p className="text-sm text-slate-500 mt-1 flex flex-wrap gap-2">
                  <span>Gerencie contratos, fotos e documentos</span>
                  <span className="hidden md:inline">•</span>
                  <span className="text-xs md:text-sm"><kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-xs">Ctrl+N</kbd> novo, <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-xs ml-1">Ctrl+K</kbd> buscar</span>
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-none">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input ref={searchInputRef} type="text" placeholder={isMobile ? 'Buscar...' : 'Buscar documentos...'} value={search} onChange={(e) => setSearch(e.target.value)}
                  className={`w-full sm:w-72 pl-9 border border-slate-200 rounded-xl text-sm focus:ring-2 outline-none ${isMobile ? 'py-3' : 'py-3 md:py-2 pr-4'}`} aria-label="Buscar documentos" />
              </div>
              <button onClick={openNewModal} className={`inline-flex items-center gap-2 text-white rounded-xl font-medium shadow-md transition-all active:scale-[0.98] ${isMobile ? 'px-4 py-3' : 'px-4 py-3 md:py-2'}`} style={{ backgroundColor: primaryColor }} aria-label="Novo documento">
                <Plus size={isMobile ? 18 : 20} /><span className={isMobile ? 'sr-only' : 'hidden sm:inline'}>Novo</span>
              </button>
            </div>
          </div>

          {/* ESTADÍSTICAS + GRÁFICO */}
          <DocumentStats stats={stats} chartData={chartData} chartGrowth={chartGrowth} showChart={showChart} onToggleChart={() => setShowChart(v => !v)} isMobile={isMobile} />

          {/* FILTROS */}
          <DocumentFilters search={search} onSearchChange={setSearch} filterType={filterType} onFilterTypeChange={setFilterType} filterClient={filterClient} onFilterClientChange={setFilterClient} filterProperty={filterProperty} onFilterPropertyChange={setFilterProperty} sortBy={sortBy} onSortByChange={setSortBy} sortOrder={sortOrder} onSortOrderChange={setSortOrder} clients={clients} properties={properties} isMobile={isMobile} showFilters={showFilters} onToggleFilters={() => setShowFilters(v => !v)} onResetFilters={resetFilters} selectedCount={selectedDocs.length} filteredCount={filteredAndSortedDocs.length} onSelectAll={selectAll} onBulkDelete={bulkDelete} searchInputRef={searchInputRef} primaryColor={primaryColor} />

          {/* GRID */}
          <DocumentGrid documents={filteredAndSortedDocs} clients={clients} properties={properties} isMobile={isMobile} primaryColor={primaryColor} selectedDocs={selectedDocs} formatFileSize={formatFileSize} onToggleSelect={toggleSelect} onToggleFavorite={toggleFavorite} onPreview={handlePreview} onDownload={handleDownload} onDelete={handleDelete} onNewDocument={openNewModal} />

          {/* FOOTER */}
          <div className={`text-center text-slate-400 pt-3 border-t flex flex-wrap justify-center gap-2 ${isMobile ? 'text-[9px]' : 'text-xs'}`}>
            <span>{filteredAndSortedDocs.length} documento(s)</span><span>•</span><span>Espaço: {stats.totalSizeMB} MB</span><span>•</span><span>{stats.largeDocs} em cloud</span>
          </div>
        </div>
      )}
    </>
  );
}